import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT } from "../artistStudio/shared";
import FanMembershipShell from "./FanMembershipShell";
import { fanMembershipApi, money, formatDate, apiErrorMessage } from "./fanMembershipApi";
import { receiptLink } from "../receipts/receiptApi";

// "My membership": the plan the fan has, its status, what was paid and until when. Cancelling keeps access until the end
// of the period that was paid for.

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 py-2 text-sm text-gray-700 border-b border-gray-100 last:border-0">
    <span className="text-gray-500">{label}</span>
    <span className="font-medium text-gray-900 text-right">{value}</span>
  </div>
);

export default function FanMembershipDetails() {
  const [state, setState] = useState({ loading: true, sub: null, error: "" });
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [notice, setNotice] = useState("");

  const load = useCallback(async () => {
    setState({ loading: true, sub: null, error: "" });
    try {
      const result = await fanMembershipApi.current();
      setState({ loading: false, sub: result?.subscription || null, error: "" });
    } catch (e) {
      setState({ loading: false, sub: null, error: apiErrorMessage(e, "Your membership could not be loaded. Please try again.") });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async () => {
    if (cancelling) return;
    setCancelling(true);
    setNotice("");
    try {
      const result = await fanMembershipApi.cancel();
      setState((s) => ({ ...s, sub: result.subscription }));
      setConfirming(false);
      setNotice("Your membership is cancelled. You keep your benefits until the end of the period you paid for.");
    } catch (e) {
      setNotice(apiErrorMessage(e, "Your membership could not be cancelled. Please try again."));
    } finally {
      setCancelling(false);
    }
  };

  const { loading, sub, error } = state;
  let body;
  if (loading) {
    body = <div className="flex justify-center py-24" role="status" aria-label="Loading membership"><Loading /></div>;
  } else if (error) {
    body = (
      <div className="text-center py-20 max-w-md mx-auto">
        <p className="text-gray-700 mb-4" role="alert">{error}</p>
        <button type="button" onClick={load} className="rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>Try again</button>
      </div>
    );
  } else if (!sub) {
    body = (
      <div className="text-center py-20 max-w-md mx-auto">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">No active membership</h1>
        <p className="text-gray-500 mb-6">Choose a plan to get unlimited streaming across Nyawoo.</p>
        <Link to="/fan-membership" className="rounded-full font-semibold text-white px-8 py-3 inline-block" style={{ backgroundColor: ACCENT }}>Choose your plan</Link>
      </div>
    );
  } else {
    const cancelled = sub.status === "cancelled";
    body = (
      <div className="max-w-xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900">Your membership</h1>
        <section aria-label="Membership details" className="mt-6 rounded-3xl border border-gray-200 p-6">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-xl font-bold text-gray-900">{sub.plan_name}</h2>
            <span className={`rounded-full text-xs font-semibold px-3 py-1 ${cancelled ? "bg-amber-50 text-amber-800" : "bg-green-50"}`} style={cancelled ? undefined : { color: ACCENT }}>
              {cancelled ? "Cancelled" : "Active"}
            </span>
          </div>
          <div className="mt-4">
            <Row label="Billing" value={sub.billing_label} />
            <Row label="Amount paid" value={money(sub.total_amount, sub.currency)} />
            <Row label="Started" value={formatDate(sub.started_at)} />
            <Row label={cancelled ? "Access ends" : "Active until"} value={formatDate(sub.current_period_end)} />
            <Row label="Renewal" value="Does not renew automatically" />
            {sub.payment_mode === "simulated" && <Row label="Payment" value="Simulated (test)" />}
          </div>
          {sub.benefits?.length > 0 && (
            <ul className="mt-5 space-y-1 text-sm text-gray-600 list-disc pl-5">
              {sub.benefits.map((b) => <li key={b}>{b}</li>)}
            </ul>
          )}
        </section>

        {notice && <p className="mt-4 text-sm text-gray-700 rounded-xl bg-gray-50 px-4 py-3" role="status">{notice}</p>}

        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/fan-membership" className="rounded-full font-semibold border-2 px-6 py-2.5" style={{ borderColor: ACCENT, color: ACCENT }}>Change plan</Link>
          {sub.id && <Link to={receiptLink("membership", sub.id)} data-testid="view-receipt" className="rounded-full font-semibold border-2 border-gray-300 text-gray-700 px-6 py-2.5">View receipt</Link>}
          {!cancelled && !confirming && (
            <button type="button" onClick={() => setConfirming(true)} className="rounded-full font-semibold border-2 border-gray-300 text-gray-700 px-6 py-2.5">Cancel membership</button>
          )}
        </div>
        {!cancelled && confirming && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" role="alertdialog" aria-label="Confirm cancellation">
            <p className="text-sm text-amber-900">Cancel your membership? You keep your benefits until {formatDate(sub.current_period_end)}.</p>
            <div className="mt-3 flex gap-3">
              <button type="button" onClick={cancel} disabled={cancelling} className="rounded-full font-semibold text-white px-5 py-2 text-sm disabled:opacity-60" style={{ backgroundColor: ACCENT }}>{cancelling ? "Cancelling…" : "Yes, cancel"}</button>
              <button type="button" onClick={() => setConfirming(false)} disabled={cancelling} className="rounded-full font-semibold border-2 border-gray-300 text-gray-700 px-5 py-2 text-sm">Keep membership</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return <FanMembershipShell>{body}</FanMembershipShell>;
}
